import { useState } from "react";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/shadcn/alert-dialog";
import { buttonVariants } from "@/components/shadcn/button";
import { deleteMessage } from "../api";
import { displayName, formatLongDate, subjectOrPlaceholder } from "../mailFormat";
import type { MailMessage } from "../types";
import { MailTaskPill } from "./MailTaskLine";

type Phase =
  | { kind: "confirm" }
  | { kind: "deleting" }
  | { kind: "blocked"; reason: string }
  | { kind: "error" };

const blockedReason = (taskCount: number): string => taskCount === 1
  ? "This email has a linked task, so it can't be deleted. Delete the task first, then delete the email."
  : `This email has ${taskCount} linked tasks, so it can't be deleted. Delete the tasks first, then delete the email.`;

type DeleteMailDialogProps = {
  message: MailMessage;
  onClose: () => void;
  onDeleted: (mailId: string) => void;
};

/**
 * Confirms deleting an email, or explains why it can't be deleted while it has tasks: a task keeps
 * a link back to the email it came from. Mount with a key per email so each starts afresh.
 */
export const DeleteMailDialog = ({ message, onClose, onDeleted }: DeleteMailDialogProps) => {
  const [phase, setPhase] = useState<Phase>(() => message.tasks.length > 0
    ? { kind: "blocked", reason: blockedReason(message.tasks.length) }
    : { kind: "confirm" });
  const isDeleting = phase.kind === "deleting";

  const confirm = async () => {
    setPhase({ kind: "deleting" });
    try {
      const result = await deleteMessage(message.id);
      if (result.status === "hasTasks") {
        setPhase({ kind: "blocked", reason: result.message });
        return;
      }
      onDeleted(message.id);
      onClose();
    } catch {
      setPhase({ kind: "error" });
    }
  };

  const onOpenChange = (open: boolean) => {
    if (!open && !isDeleting) onClose();
  };

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {phase.kind === "blocked" ? (
          <BlockedContent message={message} reason={phase.reason} />
        ) : (
          <ConfirmContent message={message} hasFailed={phase.kind === "error"} />
        )}
        <AlertDialogFooter>
          {phase.kind === "blocked" ? (
            <AlertDialogCancel>Close</AlertDialogCancel>
          ) : (
            <>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={buttonVariants({ variant: "destructive" })}
                disabled={isDeleting}
                onClick={event => {
                  // Keeps the dialog open until the delete settles, so a failure can be shown.
                  event.preventDefault();
                  void confirm();
                }}
              >
                {isDeleting ? <Loader2 className="animate-spin" /> : phase.kind === "error" ? <RefreshCw /> : <Trash2 />}
                {isDeleting ? "Deleting..." : phase.kind === "error" ? "Try again" : "Delete email"}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const EmailSummary = ({ message }: { message: MailMessage }) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
    <div className="truncate font-semibold text-gray-900">{subjectOrPlaceholder(message.subject)}</div>
    <div className="truncate text-xs text-gray-500">
      {message.from ? displayName(message.from) : "Unknown sender"} · {formatLongDate(message.dateReceivedUtc)}
    </div>
  </div>
);

const ConfirmContent = ({ message, hasFailed }: { message: MailMessage; hasFailed: boolean }) => (
  <>
    <AlertDialogHeader>
      <AlertDialogTitle>Delete this email?</AlertDialogTitle>
      <AlertDialogDescription>
        It will be removed from every email list and from engagement for everyone, and won't be synced
        again from any mailbox. This can't be undone.
      </AlertDialogDescription>
    </AlertDialogHeader>
    <EmailSummary message={message} />
    {hasFailed && (
      <p role="alert" className="m-0 text-sm text-red-700">We couldn't delete this email. Please try again.</p>
    )}
  </>
);

const BlockedContent = ({ message, reason }: { message: MailMessage; reason: string }) => {
  const now = new Date();

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>This email can't be deleted yet</AlertDialogTitle>
        <AlertDialogDescription>{reason}</AlertDialogDescription>
      </AlertDialogHeader>
      <EmailSummary message={message} />
      <p className="m-0 text-sm text-gray-600">
        A task keeps a link back to the email it was created from, so the email has to stay while the task does.
      </p>
      {message.tasks.length > 0 ? (
        <div className="space-y-1.5">
          <p className="m-0 text-sm text-gray-600">Linked tasks:</p>
          <div className="flex flex-wrap gap-1.5">
            {message.tasks.map(task => <MailTaskPill key={task.id} task={task} now={now} />)}
          </div>
          <p className="m-0 text-sm text-gray-600">Tasks can be deleted from My Diary.</p>
        </div>
      ) : (
        <p className="m-0 text-sm text-gray-600">
          You can't see the linked tasks, so ask someone who can to delete them.
        </p>
      )}
    </>
  );
};
